// pages/secure/EscrowCompliancePage.js
// One-glance escrow compliance dashboard over the trust-account records —
// 68 IAC 1450.750: separate accounts, next-business-day deposits, no negative
// ledgers, monthly reconciliation, master account log. Read-only monitor;
// the underlying entry/reconciliation CRUD lives in the existing Escrow module.

import React, { useEffect, useState } from 'react';
import EscrowStateRulesPanel from '../../components/secure/EscrowStateRulesPanel';
import EscrowDivisionPanel from '../../components/secure/EscrowDivisionPanel';
import EscrowExamPackPanel from '../../components/secure/EscrowExamPackPanel';
import { useApiClient } from '../../hooks/useApiClient';
import PageHeader from '../../components/shared/PageHeader';
import DataList from '../../components/shared/DataList';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const LEVEL = {
    violation: { badge: 'bg-red-100 text-red-700', icon: '⛔' },
    warning: { badge: 'bg-amber-100 text-amber-700', icon: '⚠️' },
};

const EscrowCompliancePage = () => {
    const apiClient = useApiClient();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        apiClient.get('/api/escrow-compliance/dashboard')
            .then(setData)
            .catch(err => setError(err.message || 'Failed to load the compliance dashboard.'));
    }, [apiClient]);

    if (error) return <div className="p-6"><p className="text-red-500">{error}</p></div>;
    if (!data) return <div className="p-6"><p>Checking escrow compliance...</p></div>;

    return (
        <div className="max-w-5xl">
            <PageHeader title="Escrow Compliance" description="Live check of the trust accounts against the escrow rules of this office's state." />

            <EscrowStateRulesPanel />

            <EscrowDivisionPanel />

            <EscrowExamPackPanel />

            {/* Headline */}
            <div className={`rounded-lg p-5 mb-6 ${data.compliant ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <p className={`text-lg font-semibold ${data.compliant ? 'text-green-700' : 'text-red-700'}`}>
                    {data.compliant ? '✓ ' : '⛔ '}{data.summary}
                </p>
            </div>

            {/* Findings */}
            {data.findings.length > 0 && (
                <div className="mb-6 space-y-2">
                    {data.findings.map((f, i) => (
                        <div key={i} className={`rounded-md px-4 py-3 flex items-start gap-3 ${LEVEL[f.level]?.badge}`}>
                            <span>{LEVEL[f.level]?.icon}</span>
                            <div>
                                <p className="font-medium">{f.message}</p>
                                <p className="text-xs opacity-70">Rule check: {f.code.replace(/_/g, ' ')}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Master escrow account log */}
            <h2 className="text-lg font-semibold text-slate-700 mb-2">Master Escrow Account Log</h2>
            <div className="bg-white rounded-lg shadow p-3 mb-6">
                <DataList rows={data.accounts} rowKey="account_id" csvName="escrow-account-log" defaultSort={{ key: 'bank_name', dir: 'asc' }}
                    empty="No escrow accounts on file — add one in the Escrow module." filters={[{ key: 'interest', label: 'Interest' }]}
                    columns={[
                        { key: 'bank_name', label: 'Bank' },
                        { key: 'account_name', label: 'Account Name' },
                        { key: 'number', label: 'Number', value: (a) => `••••${String(a.account_number).slice(-4)}`, render: (a) => <span className="font-mono text-xs">••••{String(a.account_number).slice(-4)}</span> },
                        { key: 'interest', label: 'Interest', value: (a) => (a.is_interest_bearing ? 'Interest-bearing' : 'Non-interest') },
                    ]} />
            </div>

            {/* Reconciliation currency */}
            <h2 className="text-lg font-semibold text-slate-700 mb-2">Reconciliation Status</h2>
            <div className="bg-white rounded-lg shadow p-3 mb-6">
                <DataList rows={data.reconciliation_status} rowKey="account_id" csvName="escrow-reconciliation-status" defaultSort={{ key: 'bank_name', dir: 'asc' }}
                    empty="No escrow accounts to reconcile." filters={[{ key: 'status', label: 'Status' }]}
                    columns={[
                        { key: 'bank_name', label: 'Bank' },
                        { key: 'statement_date', label: 'Last statement', type: 'date', render: (r) => (r.statement_date ? new Date(r.statement_date).toLocaleDateString() : <span className="text-amber-600">never</span>) },
                        { key: 'status', label: 'Status', value: (r) => r.status || '—' },
                        { key: 'difference', label: 'Difference', type: 'number', render: (r) => <span className={Number(r.difference) ? 'text-red-500' : ''}>{r.difference != null ? fmt(r.difference) : '—'}</span> },
                    ]} />
            </div>

            {/* Late deposits, if any */}
            {data.late_deposits.length > 0 && (
                <>
                    <h2 className="text-lg font-semibold text-red-700 mb-2">Late Deposits</h2>
                    <div className="bg-white rounded-lg shadow p-3">
                        <DataList rows={data.late_deposits} rowKey="entry_id" csvName="escrow-late-deposits" defaultSort={{ key: 'entry_date', dir: 'desc' }}
                            empty="No late deposits."
                            columns={[
                                { key: 'received_date', label: 'Received', type: 'date', render: (d) => (d.received_date ? new Date(d.received_date).toLocaleDateString() : '—') },
                                { key: 'entry_date', label: 'Deposited', type: 'date', render: (d) => <span className="text-red-600">{new Date(d.entry_date).toLocaleDateString()}</span> },
                                { key: 'payee_or_payor', label: 'From' },
                                { key: 'amount', label: 'Amount', type: 'number', render: (d) => fmt(d.amount) },
                            ]} />
                    </div>
                </>
            )}
        </div>
    );
};

export default EscrowCompliancePage;
